import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { cn } from '@personic/shared'
import { ONBOARDING_STEPS } from '@/stores/onboardingStore'

interface ProgressStepperProps {
  currentStep: number
  className?: string
}

export function ProgressStepper({ currentStep, className }: ProgressStepperProps) {
  return (
    <div className={cn('w-full', className)}>
      {/* Desktop Stepper */}
      <div className="hidden md:flex items-center justify-between">
        {ONBOARDING_STEPS.map((step, index) => {
          const isCompleted = currentStep > step.number
          const isCurrent = currentStep === step.number
          const isLast = index === ONBOARDING_STEPS.length - 1

          return (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                {/* Step Circle */}
                <motion.div
                  initial={false}
                  animate={{
                    scale: isCurrent ? 1.1 : 1,
                    backgroundColor: isCompleted
                      ? 'rgb(197, 142, 83)' // copper-500
                      : isCurrent
                      ? 'rgb(197, 142, 83)'
                      : 'rgba(255, 255, 255, 0.1)',
                  }}
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    'border-2 transition-colors duration-300',
                    isCompleted || isCurrent
                      ? 'border-copper-500'
                      : 'border-white/20'
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <span
                      className={cn(
                        'text-sm font-heading font-semibold',
                        isCurrent ? 'text-white' : 'text-white/50'
                      )}
                    >
                      {step.number}
                    </span>
                  )}
                </motion.div>

                {/* Step Name */}
                <span
                  className={cn(
                    'mt-2 text-xs font-heading',
                    isCompleted || isCurrent ? 'text-white' : 'text-white/50'
                  )}
                >
                  {step.name}
                </span>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div className="flex-1 mx-3 h-0.5 bg-white/10 relative">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: isCompleted ? '100%' : '0%' }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="absolute inset-y-0 left-0 bg-copper-500"
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Mobile Stepper */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/70 text-sm font-heading">
            Step {currentStep} of {ONBOARDING_STEPS.length}
          </span>
          <span className="text-white font-heading font-medium">
            {ONBOARDING_STEPS[currentStep - 1]?.name}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep - 1) / (ONBOARDING_STEPS.length - 1)) * 100}%` }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="h-full bg-copper-500 rounded-full"
          />
        </div>

        {/* Step Dots */}
        <div className="flex justify-between mt-2">
          {ONBOARDING_STEPS.map((step) => {
            const isCompleted = currentStep > step.number
            const isCurrent = currentStep === step.number

            return (
              <div
                key={step.number}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors duration-300',
                  isCompleted
                    ? 'bg-copper-500'
                    : isCurrent
                    ? 'bg-copper-400'
                    : 'bg-white/20'
                )}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
